import { getDb } from './schema.js';
import { useAuthStore } from '@readsavor/auth';
// A no-op collection that simulates Dexie.Collection but does nothing.
const noopCollection = {
    toArray: () => Promise.resolve([]),
    first: () => Promise.resolve(undefined),
    last: () => Promise.resolve(undefined),
    count: () => Promise.resolve(0),
    // Add other collection methods if needed, all returning no-op promises.
};
// A no-op table that simulates Dexie.Table but does nothing.
// This is returned when the user is not authenticated.
const noopTable = {
    get: () => Promise.resolve(undefined),
    put: () => Promise.resolve(undefined),
    add: () => Promise.resolve(undefined),
    update: () => Promise.resolve(undefined),
    delete: () => Promise.resolve(0),
    clear: () => Promise.resolve(),
    bulkAdd: () => Promise.resolve(undefined),
    bulkPut: () => Promise.resolve(undefined),
    bulkDelete: () => Promise.resolve(0),
    where: () => noopCollection,
    toCollection: () => noopCollection,
    // This allows chaining like `db.table.where(...).toArray()`
    // Add other table methods if needed.
};
class DatabaseManager {
    getDbInstance() {
        const session = useAuthStore.getState().session;
        const userId = session?.user?.id;
        if (!userId) {
            // Return null if the user is not authenticated.
            return null;
        }
        return getDb(userId);
    }
    get textDocuments() {
        return this.getDbInstance()?.textDocuments ?? noopTable;
    }
    get textParagraphs() {
        return this.getDbInstance()?.textParagraphs ?? noopTable;
    }
    get textSelectionTranslations() {
        return this.getDbInstance()?.textSelectionTranslations ?? noopTable;
    }
    get pdfDocuments() {
        return this.getDbInstance()?.pdfDocuments ?? noopTable;
    }
    get pdfPageInfos() {
        return this.getDbInstance()?.pdfPageInfos ?? noopTable;
    }
    get pdfParagraphs() {
        return this.getDbInstance()?.pdfParagraphs ?? noopTable;
    }
    /**
     * Executes a transaction on the currently active user's database.
     * If the user is not authenticated, the transaction will not run.
     * @param mode Transaction mode ('r', 'rw', etc.)
     * @param tables An array of table names (e.g., ['pdfDocuments', 'pdfPageInfos'])
     * @param callback The transaction callback function.
     */
    transaction(mode, tables, callback) {
        const db = this.getDbInstance();
        if (!db) {
            // If there's no DB (user not logged in), do not execute the transaction.
            // Return a resolved promise to prevent uncaught promise rejections.
            return Promise.resolve(undefined);
        }
        const tableInstances = tables.map(tableName => db.table(tableName));
        return db.transaction(mode, tableInstances, callback);
    }
    /**
     * Connects to the current user's database.
     * If the user is not authenticated, this is a no-op.
     */
    async connect() {
        const db = this.getDbInstance();
        if (db) {
            await db.connect();
        }
    }
}
export const dbManager = new DatabaseManager();
//# sourceMappingURL=DatabaseManager.js.map